import { useState, useEffect } from "react";
import axios from "axios";
import { API_URL, getAuthHeaders } from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Settings, Save, Clock, Calendar, Users, Map } from "lucide-react";
import { toast } from "sonner";

export default function CompanySettings() {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [settings, setSettings] = useState({
    overtime_threshold_hours: 8.0,
    allow_holiday_work: true,
    allow_multiple_shifts: true,
    holiday_pay_multiplier: 2.0,
    overtime_pay_multiplier: 1.5,
    map_refresh_interval_seconds: 5,
  });

  const canManageSettings = () => {
    return user?.role === "admin" || user?.role === "revendedor" || user?.role === "supervisor";
  };

  const canSelectCompany = () => {
    return user?.role === "admin" || user?.role === "revendedor";
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompanyId) {
      fetchCompanySettings();
    }
  }, [selectedCompanyId]);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API_URL}/companies`, {
        headers: getAuthHeaders(),
      });
      setCompanies(response.data);
      
      // Si es supervisor, usar su company_id directamente
      if (user?.role === "supervisor" && user?.company_id) {
        setSelectedCompanyId(user.company_id);
      }
      // Para admin y revendedor, seleccionar la primera compañía disponible
      else if (response.data.length > 0 && !selectedCompanyId) {
        setSelectedCompanyId(response.data[0].id);
      }
    } catch (error) {
      console.error("Error al cargar compañías:", error);
      toast.error("Error al cargar compañías");
    }
  };

  const fetchCompanySettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/companies/${selectedCompanyId}`, {
        headers: getAuthHeaders(),
      });
      
      const company = response.data;
      setSettings({
        overtime_threshold_hours: company.overtime_threshold_hours ?? 8.0,
        allow_holiday_work: company.allow_holiday_work ?? true,
        allow_multiple_shifts: company.allow_multiple_shifts ?? true,
        holiday_pay_multiplier: company.holiday_pay_multiplier ?? 2.0,
        overtime_pay_multiplier: company.overtime_pay_multiplier ?? 1.5,
        map_refresh_interval_seconds: company.map_refresh_interval_seconds ?? 5,
      });
    } catch (error) {
      console.error("Error al cargar configuración:", error);
      toast.error("Error al cargar configuración");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedCompanyId) {
      toast.error("Seleccione una compañía");
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/companies/${selectedCompanyId}`,
        settings,
        { headers: getAuthHeaders() }
      );
      toast.success("Configuración guardada exitosamente");
    } catch (error) {
      console.error("Error al guardar configuración:", error);
      toast.error(error.response?.data?.detail || "Error al guardar configuración");
    } finally {
      setSaving(false);
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const selectedCompany = companies.find(c => c.id === selectedCompanyId);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">
            Configuración de Compañía
          </h1>
          <p className="text-slate-600 mt-1">
            Gestiona las políticas de horarios y nómina
          </p>
        </div>
      </div>

      {/* Company Selector - Solo para Admin y Revendedor */}
      {canSelectCompany() && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <Label className="text-sm font-medium whitespace-nowrap">
                Compañía:
              </Label>
              <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
                <SelectTrigger className="w-full max-w-md">
                  <SelectValue placeholder="Seleccione una compañía" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mostrar nombre de compañía para Supervisor */}
      {user?.role === "supervisor" && selectedCompany && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <Label className="text-sm font-medium whitespace-nowrap">
                Compañía:
              </Label>
              <span className="text-lg font-semibold text-slate-900">
                {selectedCompany.name}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Settings Cards */}
      {selectedCompanyId && !loading && (
        <>
          {/* Horarios y Horas Extras */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Horarios y Horas Extras
              </CardTitle>
              <CardDescription>
                Configura las políticas de horarios laborales
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="overtime_threshold">
                    Horas para considerar extras
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="overtime_threshold"
                      type="number"
                      step="0.5"
                      min="0"
                      value={settings.overtime_threshold_hours}
                      onChange={(e) => handleSettingChange('overtime_threshold_hours', parseFloat(e.target.value))}
                      className="max-w-xs"
                    />
                    <span className="text-sm text-slate-600">horas</span>
                  </div>
                  <p className="text-sm text-slate-500">
                    Las horas trabajadas que excedan este límite se considerarán horas extras
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="overtime_multiplier">
                    Multiplicador de pago (horas extras)
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="overtime_multiplier"
                      type="number"
                      step="0.1"
                      min="1"
                      value={settings.overtime_pay_multiplier}
                      onChange={(e) => handleSettingChange('overtime_pay_multiplier', parseFloat(e.target.value))}
                      className="max-w-xs"
                    />
                    <span className="text-sm text-slate-600">x</span>
                  </div>
                  <p className="text-sm text-slate-500">
                    Factor por el cual se multiplica el pago de horas extras (ej: 1.5 = 150%)
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Días Feriados */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Días Feriados
              </CardTitle>
              <CardDescription>
                Configura las políticas para días festivos
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="allow_holiday_work">
                    Permitir trabajar en días feriados
                  </Label>
                  <p className="text-sm text-slate-500">
                    Los empleados pueden registrar horas en días marcados como feriados
                  </p>
                </div>
                <Switch
                  id="allow_holiday_work"
                  checked={settings.allow_holiday_work}
                  onCheckedChange={(checked) => handleSettingChange('allow_holiday_work', checked)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="holiday_multiplier">
                  Multiplicador de pago (días feriados)
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="holiday_multiplier"
                    type="number"
                    step="0.1"
                    min="1"
                    value={settings.holiday_pay_multiplier}
                    onChange={(e) => handleSettingChange('holiday_pay_multiplier', parseFloat(e.target.value))}
                    className="max-w-xs"
                  />
                  <span className="text-sm text-slate-600">x</span>
                </div>
                <p className="text-sm text-slate-500">
                  Factor por el cual se multiplica el pago en días feriados (ej: 2.0 = 200%)
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Turnos */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Gestión de Turnos
              </CardTitle>
              <CardDescription>
                Configura las políticas de turnos de trabajo
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="allow_multiple_shifts">
                    Permitir múltiples turnos por día
                  </Label>
                  <p className="text-sm text-slate-500">
                    Los empleados pueden iniciar más de un turno en el mismo día
                  </p>
                </div>
                <Switch
                  id="allow_multiple_shifts"
                  checked={settings.allow_multiple_shifts}
                  onCheckedChange={(checked) => handleSettingChange('allow_multiple_shifts', checked)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Configuración de Mapa - Solo para Admin */}
          {user?.role === "admin" && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Map className="h-5 w-5" />
                  Actualización de Mapa en Vivo
                </CardTitle>
                <CardDescription>
                  Configura el intervalo de actualización del mapa de vigilantes activos
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="map_refresh_interval">
                    Intervalo de actualización
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="map_refresh_interval"
                      type="number"
                      step="1"
                      min="1"
                      max="60"
                      value={settings.map_refresh_interval_seconds}
                      onChange={(e) => handleSettingChange('map_refresh_interval_seconds', parseInt(e.target.value) || 5)}
                      className="max-w-xs"
                    />
                    <span className="text-sm text-slate-600">segundos</span>
                  </div>
                  <p className="text-sm text-slate-500">
                    Cada cuántos segundos se actualiza automáticamente la ubicación de los vigilantes en el mapa (mínimo: 1, máximo: 60)
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving}
              size="lg"
              className="gap-2"
            >
              <Save className="h-4 w-4" />
              {saving ? "Guardando..." : "Guardar Configuración"}
            </Button>
          </div>
        </>
      )}

      {loading && (
        <div className="text-center py-12">
          <p className="text-slate-500">Cargando configuración...</p>
        </div>
      )}
    </div>
  );
}
